import type { RbacV2AbilityTuple } from './rbac.v2.js'

/**
 * Contrat des exports du back-office en tâche de fond (chantier S8, lot S8.3, conception §3) :
 * statut d'un export, réponse `202` des routes d'export, routes `/exports`, évènement temps réel
 * et catalogue des types. Partagé par le backend (`app/services/exports/`) et le back-office
 * (hook d'export et écran « Exports », S8.4 et S8.5).
 */

export const EXPORT_STATUSES = [
  'pending',
  'running',
  'ready',
  'failed',
  'cancelled',
  'expired',
] as const

export type ExportStatus = (typeof EXPORT_STATUSES)[number]

/** Calcul en file ou en cours : seuls statuts qu'une annulation arrête. */
export const EXPORT_IN_FLIGHT_STATUSES = ['pending', 'running'] as const satisfies ExportStatus[]

export type ExportFormat = 'xlsx' | 'csv' | 'zip' | 'pdf'

/** `scope` : partagé dans le périmètre de boutiques ; `requester` : réservé à ses demandeurs. */
export type ExportVisibility = 'scope' | 'requester'

/** Durée de conservation du fichier, `EXPORT_RETENTION_DAYS` (arbitrage 20). */
export type ExportRetention = 'short' | 'standard' | 'long'

/** Données personnelles, cas général, comptabilité : affiché par l'écran « Exports ». */
export const EXPORT_RETENTION_DAYS: Record<ExportRetention, number> = {
  short: 7,
  standard: 30,
  long: 90,
}

/**
 * Issue d'une demande : `created` (calcul lancé), `joined` (calcul identique déjà en cours),
 * `reused` (fichier prêt resservi).
 */
export type ExportRequestOutcome = 'created' | 'joined' | 'reused'

export interface ExportSummary {
  /** `public_id`, seul identifiant exposé hors du backend. */
  id: string
  type: string
  /** Libellé du catalogue (« Bilan comptable »), sinon la clé du type. */
  label: string
  status: ExportStatus
  format: ExportFormat
  /** « 01/09/2026 → 30/09/2026 · 12 boutiques » : jamais de donnée personnelle. */
  paramsSummary: string
  /** Période couverte, bornes incluses, en dates de Paris `aaaa-mm-jj`. */
  period: { start: string; end: string } | null
  /** Boutiques couvertes ; `null` : export sans dimension boutique. */
  storeCount: number | null
  fileName: string
  sizeBytes: number | null
  rowCount: number | null
  /** Lignes écrites pendant le calcul. */
  progress: { rows: number } | null
  createdAt: string
  finishedAt: string | null
  /** `null` : fichier immuable (période close) ; passé : le fichier n'est plus resservi. */
  freshUntil: string | null
  /** Fin de disponibilité du fichier. */
  expiresAt: string | null
  retention: ExportRetention
  containsPii: boolean
  visibility: ExportVisibility
  requestedBy: { id: number; name: string } | null
  /** Message en français d'un export en échec. */
  error: string | null
  /**
   * L'admin détient le droit d'ouvrir le fichier (garde et boutiques du moment). Faux pour un
   * export que seule la supervision (`read admin-tools.exports.export`) lui montre.
   */
  canDownload: boolean
}

/**
 * Corps de la réponse `202` d'une route d'export appelée avec `Prefer: respond-async`, y compris
 * pour un fichier déjà prêt (`outcome: 'reused'`). L'en-tête `Location` n'est pas exposé par CORS :
 * le back-office lit `export.id`.
 */
export interface ExportRequestResponse {
  export: ExportSummary
  outcome: ExportRequestOutcome
}

/**
 * Codes des refus propres aux routes `/exports` (contrat `{ code, message }`, S5.4a) : le back-office
 * y lit l'action à proposer. Un export introuvable rend le `not_found` générique.
 */
export const EXPORT_ERROR_CODES = {
  /** 409 : le fichier n'est pas encore prêt. */
  notReady: 'export_not_ready',
  /** 410 : le fichier a expiré, « Relancer ». */
  expired: 'export_expired',
  /** 410 : le type n'existe plus dans le registre. */
  typeRetired: 'export_type_retired',
  /** 409 : le type n'est plus servi en tâche de fond (drapeau `exports-async`). */
  typeInactive: 'export_type_inactive',
  /** 409 : le calcul n'est plus en file ni en cours. */
  notInFlight: 'export_not_in_flight',
} as const

/** En-tête `Prefer` qui fait répondre une route d'export en tâche de fond (RFC 7240). */
export const EXPORT_PREFER_ASYNC = 'respond-async'

/** Onglets de l'écran « Exports » : demandes de l'admin, ou tout ce que ses droits couvrent. */
export const EXPORT_LIST_VIEWS = ['mine', 'available'] as const

export type ExportListView = (typeof EXPORT_LIST_VIEWS)[number]

/** Filtre de fraîcheur : `fresh` encore resservi, `stale` périmé. */
export const EXPORT_FRESHNESS_FILTERS = ['fresh', 'stale'] as const

export type ExportFreshnessFilter = (typeof EXPORT_FRESHNESS_FILTERS)[number]

/** `GET /exports`. */
export interface ExportListResponse {
  items: ExportSummary[]
  total: number
  page: number
  pageSize: number
}

/** `GET /exports/:publicId/download` : URL présignée de 60 s, jamais journalisée. */
export interface ExportDownloadResponse {
  url: string
  expiresAt: string
  fileName: string
}

/** `GET /exports/active-types` : types que le backend sert en tâche de fond pour cet admin. */
export interface ExportActiveTypesResponse {
  types: string[]
}

/** Évènement `export:updated`, vers la room `admin:<id>` de chaque demandeur. Jamais d'URL. */
export interface ExportUpdatedPayload {
  /** `public_id`. */
  id: string
  status: ExportStatus
  type: string
  fileName: string
}

/** Garde d'un type, celle de sa route d'export : un tuple, un ET (tableau) ou un OU (`{ any }`). */
export type ExportTypePermission =
  | RbacV2AbilityTuple
  | RbacV2AbilityTuple[]
  | { any: RbacV2AbilityTuple[] }

export interface ExportTypeInfo {
  label: string
  permission: ExportTypePermission
}

/**
 * Types d'export servis en tâche de fond, avec leur libellé et leur garde. Chaque type du
 * registre du backend (`app/services/exports/registry.ts`) y figure avec la même garde, vérifié
 * au chargement du registre ; l'écran « Exports » (S8.5) lit ces gardes pour son entrée de menu.
 * Vide jusqu'au pilote (S8.6).
 */
export const EXPORT_TYPES: Readonly<Record<string, ExportTypeInfo>> = {}

/**
 * Supervision des exports (`admin-tools.exports.export`) : `read` voit tous les exports, `edit`
 * régénère un fichier jugé faux (motif obligatoire, arbitrage 22), `delete` annule et purge ceux
 * des autres.
 */
export const EXPORT_SUPERVISION_READ: RbacV2AbilityTuple = ['read', 'admin-tools.exports.export']
export const EXPORT_SUPERVISION_REGENERATE: RbacV2AbilityTuple = [
  'edit',
  'admin-tools.exports.export',
]
export const EXPORT_SUPERVISION_DELETE: RbacV2AbilityTuple = [
  'delete',
  'admin-tools.exports.export',
]
